import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, TruncatedText } from '@/shared/ui';
import { XIcon } from 'lucide-react';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { withSecondsSuffix } from '@/entities/attributes';
import { useTimeDisplayFormat } from '@/entities/settings';
import type { AttributeFilter } from '@/entities/dashboards';
import { getAttributeFilterKind } from '../model/attributeFilters';
import { AttributeFilterField } from './AttributeFilterField';

type Props = {
  /** Атрибут из системной конфигурации; может быть undefined для «осиротевшего» фильтра */
  attribute: ConversationAttributeItem | undefined;
  /** Текущее значение фильтра */
  filter: AttributeFilter;
  /** Обновление фильтра */
  onChange: (next: AttributeFilter) => void;
  /** Удаление атрибута из активной панели фильтров */
  onRemove: () => void;
};

/**
 * Ячейка одного атрибутного фильтра. Размещается в сетке фильтров экрана-потребителя.
 * Лейбл — имя атрибута, компонент ввода выбирается по `Attribute.dataType`.
 */
export const AttributeFilterCell: FC<Props> = ({ attribute, filter, onChange, onRemove }) => {
  const { t } = useTranslation('common');
  const timeDisplayFormat = useTimeDisplayFormat();
  const label = withSecondsSuffix(
    attribute?.name || attribute?.pathDescription || filter.path,
    attribute,
    timeDisplayFormat,
    t('secondsSuffix'),
  );
  const kind = getAttributeFilterKind(attribute);

  // Шапка ячейки: имя атрибута + кнопка удаления
  const header = (
    <div className="flex items-center justify-between gap-2 min-h-6">
      <TruncatedText className="text-sm font-medium">{label}</TruncatedText>
      <IconButton
        variant="ghost"
        size="icon"
        icon={<XIcon />}
        label={t('attributeFilters.removeAttribute')}
        onClick={onRemove}
      />
    </div>
  );

  if (!attribute || kind === null) {
    return <div className="flex flex-col gap-1">{header}</div>;
  }

  return (
    <div className="flex flex-col gap-1">
      {header}
      <AttributeFilterField attribute={attribute} filter={filter} onChange={onChange} />
    </div>
  );
};
